// Direct upload to Azure Storage:
//  1. ask Contracts for an upload SAS (only metadata travels to the API, never the binary)
//  2. PUT the binary straight to Azure with the SAS URL
//  3. the owner service (MedicalProtocols / Settlements) registers the file from the storage event;
//     the registered files are read from that owner service.
import {
  AzureFileLinkKind,
  AzureFileListLoaderKind,
  OwnerFileViewModel,
  ProviderFileLinkRequest,
  ProviderFileTypeEnum,
  ProviderFileViewModel,
  SponsorFileLinkRequest,
  ServiceFileLinkRequest,
  ServiceFileViewModel,
  SettlementFileViewModel,
  PaymentReceiptViewModel,
  CreditorInvoiceFileViewModel,
  SponsorFileViewModel,
  UploadSasToken,
  UploadSasTokenRequest,
} from '../models/Entities/AzureFiles/AzureFileUpload';
import { ErrorType } from '../models/HttpError';
import { IResult } from '../models/HttpResult';
import { getUploadContentType } from '../functions/AzureFileUpload.Functions';

//Functions
import * as Functions from '../functions/common';

// Error Tracer
import * as Sentry from '@sentry/react';

const Gateway = process.env.REACT_APP_BILLER_API_GATEWAY;

// The gateway maps /contracts/{everything} → Contracts API /contracts/{everything};
// the API route is "contracts/files/sas-token" (same convention as `${gateway}contracts/{id}/files`).
export const SasTokenURL = (): string => `${Gateway}contracts/files/sas-token`;
// The gateway maps /protocols/{everything} → MedicalProtocols API /{everything}
export const ProviderFilesURL = (providerId: number): string =>
  `${Gateway}protocols/providers/${providerId}/files?PageNumber=1&PageSize=100`;
export const ProtocolSponsorFilesURL = (protocolId: number): string =>
  `${Gateway}protocols/protocols/${protocolId}/sponsor-files?PageNumber=1&PageSize=100`;
// The gateway maps /settlements/{everything} → Settlements API /{everything}
export const SettlementFilesURL = (settlementId: number): string =>
  `${Gateway}settlements/settlements/${settlementId}/files`;
// MedicalProtocols service receipts (contract §10.3): GET protocols/services/{serviceId:guid}/files
export const ServiceFilesURL = (serviceId: string): string =>
  `${Gateway}protocols/services/${encodeURIComponent(serviceId)}/files`;

// ---- direct registration / links (contract §9.3 and §9.5) ----
/** Base url of the registered files of an owner (without query string). */
export const OwnerFilesBaseURL = (kind: AzureFileListLoaderKind, entityId: number | string): string => {
  switch (kind) {
    // The gateway maps /cashflow/{everything} → CashFlow API /{everything} (contract §12.2)
    case 'paymentReceipt':
      return `${Gateway}cashflow/payments/${encodeURIComponent(String(entityId))}/receipts`;
    case 'creditorInvoice':
      return `${Gateway}cashflow/creditors/${encodeURIComponent(String(entityId))}/invoices`;
    case 'service':
      return ServiceFilesURL(String(entityId));
    case 'provider':
      return `${Gateway}protocols/providers/${entityId}/files`;
    case 'protocolSponsor':
      return `${Gateway}protocols/protocols/${entityId}/sponsor-files`;
    case 'settlement':
    default:
      return `${Gateway}settlements/settlements/${entityId}/files`;
  }
};
/** Creditor invoices register with POST creditors/{guid}/invoices (no /register suffix). */
export const RegisterFileURL = (kind: AzureFileListLoaderKind, entityId: number | string): string =>
  kind === 'creditorInvoice' ? OwnerFilesBaseURL(kind, entityId) : `${OwnerFilesBaseURL(kind, entityId)}/register`;
export const LinkFileURL = (kind: AzureFileListLoaderKind, entityId: number | string): string =>
  `${OwnerFilesBaseURL(kind, entityId)}/link`;
export const UnlinkFileURL = (kind: AzureFileListLoaderKind, entityId: number | string, fileId: string): string =>
  `${OwnerFilesBaseURL(kind, entityId)}/${encodeURIComponent(fileId)}`;

const buildError = (code: number, message: string, errors: string[] = []): ErrorType => ({
  ErrorCode: code,
  ErrorMessage: message,
  Errors: errors,
});

const toErrorType = async (response: Response): Promise<ErrorType> => {
  const body = await response.text().catch(() => '');
  return Functions.HttpErrorHandler(body || null, response);
};

const isErrorType = (error: unknown): error is ErrorType =>
  typeof error === 'object' && error !== null && 'ErrorCode' in error;

const normalizeError = (error: unknown): ErrorType => {
  if (isErrorType(error)) return error;
  const message = error instanceof Error ? error.message : 'Network error';
  return buildError(0, message);
};

const getJson = async <T>(url: string, token: string): Promise<T> => {
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    });
    if (!response.ok) {
      Sentry.captureException(response);
      throw await toErrorType(response);
    }
    const result = (await response.json()) as IResult<T>;
    return result?.data;
  } catch (error: unknown) {
    Sentry.captureException(error);
    throw normalizeError(error);
  }
};

/** POST contracts/files/sas-token → Response<ContractFileSasTokenViewModel> */
export const requestUploadSas = async (token: string, request: UploadSasTokenRequest): Promise<UploadSasToken> => {
  try {
    const response = await fetch(SasTokenURL(), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      Sentry.captureException(response);
      throw await toErrorType(response);
    }
    const result = (await response.json()) as IResult<UploadSasToken>;
    if (!result?.succeeded || !result?.data?.sasUrl) {
      throw buildError(response.status, result?.message || 'SAS token not received', result?.errors ?? []);
    }
    return result.data;
  } catch (error: unknown) {
    Sentry.captureException(error);
    throw normalizeError(error);
  }
};

/**
 * Message for a failed PUT to Azure Storage, including the storage error code and, for 403, the
 * authentication detail (e.g. signature mismatch or SAS not yet valid) so the cause is visible to the user.
 */
export const describeAzureStorageError = (status: number, responseText?: string | null): string => {
  const body = responseText ?? '';
  const code = body.match(/<Code>([\s\S]*?)<\/Code>/)?.[1]?.trim();
  const detail = body.match(/<AuthenticationErrorDetail>([\s\S]*?)<\/AuthenticationErrorDetail>/)?.[1]?.trim();
  const message = body.match(/<Message>([\s\S]*?)<\/Message>/)?.[1]?.split('\n')[0]?.trim();
  const parts = [`Azure Storage upload failed (${status}${code ? ` ${code}` : ''})`];
  if (detail) parts.push(detail);
  else if (message) parts.push(message);
  return parts.join(': ');
};

/** PUT {sasUrl} with the binary, reporting progress (0..100). */
export const uploadToAzure = (
  sasUrl: string,
  file: File,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal
): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(buildError(0, 'Upload aborted'));
      return;
    }

    const xhr = new XMLHttpRequest();

    if (onProgress) {
      xhr.upload.onprogress = (event: ProgressEvent) => {
        if (event.lengthComputable && event.total > 0) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
      } else {
        reject(buildError(xhr.status, describeAzureStorageError(xhr.status, xhr.responseText)));
      }
    };
    xhr.onerror = () => reject(buildError(0, 'Network error during upload'));
    xhr.onabort = () => reject(buildError(0, 'Upload aborted'));

    signal?.addEventListener('abort', () => xhr.abort());

    xhr.open('PUT', sasUrl);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', getUploadContentType(file));
    xhr.send(file);
  });

/** MedicalProtocols: GET providers/{id}/files */
export const getProviderFiles = async (token: string, providerId: number): Promise<ProviderFileViewModel[]> =>
  (await getJson<ProviderFileViewModel[]>(ProviderFilesURL(providerId), token)) ?? [];

/** MedicalProtocols: GET protocols/{id}/sponsor-files */
export const getProtocolSponsorFiles = async (token: string, protocolId: number): Promise<SponsorFileViewModel[]> =>
  (await getJson<SponsorFileViewModel[]>(ProtocolSponsorFilesURL(protocolId), token)) ?? [];

/** Settlements: GET settlements/{id}/files */
export const getSettlementFiles = async (token: string, settlementId: number): Promise<SettlementFileViewModel[]> =>
  (await getJson<SettlementFileViewModel[]>(SettlementFilesURL(settlementId), token)) ?? [];

/** MedicalProtocols: GET services/{id}/files (service receipts, contract §10.3) */
export const getServiceFiles = async (token: string, serviceId: string): Promise<ServiceFileViewModel[]> =>
  (await getJson<ServiceFileViewModel[]>(ServiceFilesURL(serviceId), token)) ?? [];

/** CashFlow: GET payments/{id}/receipts (contract §12.2) */
export const getPaymentReceipts = async (token: string, paymentId: number): Promise<PaymentReceiptViewModel[]> =>
  (await getJson<PaymentReceiptViewModel[]>(OwnerFilesBaseURL('paymentReceipt', paymentId), token)) ?? [];

/** CashFlow: GET creditors/{guid}/invoices, seen as registered files */
export const getCreditorInvoiceFiles = async (token: string, creditorId: string): Promise<CreditorInvoiceFileViewModel[]> =>
  (await getJson<CreditorInvoiceFileViewModel[]>(OwnerFilesBaseURL('creditorInvoice', creditorId), token)) ?? [];

/** POST/DELETE with JSON; resolves with `data` (may be undefined), rejects with ErrorType. */
const sendJson = async <T>(url: string, method: 'POST' | 'DELETE', token: string, body?: unknown): Promise<T | undefined> => {
  try {
    const response = await fetch(url, {
      method,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!response.ok) {
      Sentry.captureException(response);
      throw await toErrorType(response);
    }
    const text = await response.text().catch(() => '');
    if (!text) return undefined;
    let result: IResult<T> | undefined;
    try {
      result = JSON.parse(text) as IResult<T>;
    } catch {
      return undefined;
    }
    if (result && result.succeeded === false) {
      throw buildError(response.status, result.message || 'Operation failed', result.errors ?? []);
    }
    return result?.data;
  } catch (error: unknown) {
    Sentry.captureException(error);
    throw normalizeError(error);
  }
};

/**
 * Registers an uploaded blob in its owner service right after the PUT (contract §9.5):
 * provider → POST providers/{id}/files/register, protocol → POST protocols/{id}/sponsor-files/register,
 * settlement → POST settlements/{id}/files/register, service → POST services/{guid}/files/register.
 * `path` is relative to the tenant.
 */
export const registerFile = async (
  token: string,
  kind: AzureFileListLoaderKind,
  entityId: number | string,
  path: string,
  extra?: Record<string, unknown>
): Promise<OwnerFileViewModel | undefined> =>
  sendJson<OwnerFileViewModel>(
    RegisterFileURL(kind, entityId),
    'POST',
    token,
    kind === 'creditorInvoice' ? { source: 1, ...(extra ?? {}), path } : { ...(extra ?? {}), path }
  );

/**
 * Links an existing storage file (contract §9.3 / §10.3). Settlement body: { path }; protocol body: { path, sponsorId };
 * service body: { path, sponsorId? }; provider body (spec 005): { path, fileType? }. 409 → already linked.
 */
export const linkFile = async (
  token: string,
  kind: AzureFileLinkKind,
  entityId: number | string,
  path: string,
  sponsorId?: number,
  fileType?: ProviderFileTypeEnum
): Promise<OwnerFileViewModel | undefined> => {
  let body: SponsorFileLinkRequest | ServiceFileLinkRequest | ProviderFileLinkRequest | { path: string } = { path };
  if (kind === 'protocolSponsor') body = { path, sponsorId: sponsorId ?? 0 };
  if (kind === 'service' && sponsorId !== undefined && sponsorId > 0) body = { path, sponsorId };
  if (kind === 'provider' && fileType !== undefined) body = { path, fileType };
  return sendJson<OwnerFileViewModel>(LinkFileURL(kind, entityId), 'POST', token, body);
};

/** Unlinks a linked file (logical delete of the link; the blob is never deleted). */
export const unlinkFile = async (
  token: string,
  kind: AzureFileLinkKind,
  entityId: number | string,
  fileId: string
): Promise<void> => {
  await sendJson<unknown>(UnlinkFileURL(kind, entityId, fileId), 'DELETE', token);
};
